import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * Auth0 Callback Component
 *
 * Handles Auth0 authentication callback and redirects users appropriately.
 * Invite redemption is handled globally in App.tsx so it runs regardless of
 * which route Auth0 redirects back to.
 */
export const Auth0Callback: React.FC = () => {
  const { isLoading, isAuthenticated, error, user } = useAuth0();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (!isLoading) {
      if (isAuthenticated && user) {
        setStatus('success');

        // Deep-link restore is handled by onRedirectCallback (main.tsx) via
        // appState.returnTo; this fallback only fires if the user is still on
        // /callback after that (e.g. landed here directly).
        timeoutId = setTimeout(() => {
          navigate('/', { replace: true });
        }, 1500);
      } else if (error) {
        setStatus('error');
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading, isAuthenticated, user, error, navigate]);

  // Still processing authentication
  if (isLoading || status === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto h-12 w-12 rounded bg-surface flex items-center justify-center">
              <Spinner size="lg" className="text-foreground" />
            </div>
            <div className="space-y-2">
              <CardTitle>Authenticating</CardTitle>
              <CardDescription>
                Please wait while we sign you in...
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="text-center">
            <div className="space-y-2">
              <div className="h-1 w-full bg-surface rounded overflow-hidden">
                <div className="h-full bg-foreground rounded animate-pulse" style={{ width: '60%' }}></div>
              </div>
              <p className="text-sm text-muted-foreground">
                Completing authentication with Auth0...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Authentication successful
  if (status === 'success' && isAuthenticated && user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto h-12 w-12 rounded bg-success/15 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 stroke-[1.5] text-success" />
            </div>
            <div className="space-y-2">
              <CardTitle>Welcome Back!</CardTitle>
              <CardDescription>
                Authentication successful. Redirecting to your sites...
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="flex items-center justify-center gap-3">
              {user.picture && (
                <img
                  src={user.picture}
                  alt="User avatar"
                  className="h-8 w-8 rounded-full"
                />
              )}
              <div className="text-left">
                <p className="text-sm font-medium">
                  {user.name || user.email}
                </p>
                {user.name && user.email && (
                  <p className="text-sm text-muted-foreground">
                    {user.email}
                  </p>
                )}
              </div>
            </div>
            <div className="h-1 w-full bg-surface rounded overflow-hidden">
              <div className="h-full bg-success rounded animate-pulse" style={{ width: '100%' }}></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Authentication error
  if (status === 'error' || error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto h-12 w-12 rounded bg-destructive/15 flex items-center justify-center">
              <XCircle className="h-6 w-6 stroke-[1.5] text-destructive" />
            </div>
            <div className="space-y-2">
              <CardTitle>Authentication Failed</CardTitle>
              <CardDescription>
                We encountered an issue signing you in
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription className="text-sm">
                  <strong>Error:</strong> {error.message}
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Button size="dense"
                variant="primary"
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Try Again
              </Button>
              <Button size="dense"
                variant="tertiary"
                onClick={() => navigate('/')}
                className="w-full"
              >
                Go Home
              </Button>
            </div>
            <p className="text-sm text-center text-muted-foreground">
              If this problem persists, please contact support.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Fallback state (should not reach here)
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Processing...</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <Button size="dense" variant="primary" onClick={() => navigate('/login')}>
            Return to Login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth0Callback;